import ViewListIcon from '@mui/icons-material/ViewList';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import type { ReactNode } from 'react';
import { cn } from '@/utils/cn';

export type ViewMode = 'list' | 'calendar' | 'hierarchy';

export interface ViewModeOption<T extends string = ViewMode> {
  value: T;
  label: string;
  icon?: ReactNode;
}

export interface ViewModeToggleProps<T extends string = ViewMode> {
  value: T;
  onChange: (mode: T) => void;
  className?: string;
  options?: ViewModeOption<T>[];
  'aria-label'?: string;
}

const DEFAULT_OPTIONS: ViewModeOption<ViewMode>[] = [
  { value: 'list', label: 'List', icon: <ViewListIcon sx={{ fontSize: 18 }} /> },
  { value: 'calendar', label: 'Calendar', icon: <CalendarMonthIcon sx={{ fontSize: 18 }} /> },
];

const HIERARCHY_OPTIONS: ViewModeOption<'list' | 'hierarchy'>[] = [
  { value: 'list', label: 'List', icon: <ViewListIcon sx={{ fontSize: 18 }} /> },
  { value: 'hierarchy', label: 'Hierarchy', icon: <AccountTreeIcon sx={{ fontSize: 18 }} /> },
];

export function ViewModeToggle<T extends string = ViewMode>({
  value,
  onChange,
  className,
  options,
  'aria-label': ariaLabel = 'View mode',
}: ViewModeToggleProps<T>) {
  const resolved = (options ?? DEFAULT_OPTIONS) as ViewModeOption<T>[];

  return (
    <div
      className={cn(
        'inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5 dark:border-slate-700 dark:bg-slate-800',
        className,
      )}
      role="group"
      aria-label={ariaLabel}
    >
      {resolved.map((opt) => (
        <button
          key={opt.value}
          type="button"
          className={cn(
            'inline-flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
            value === opt.value
              ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-900 dark:text-slate-100'
              : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200',
          )}
          onClick={() => onChange(opt.value)}
        >
          {opt.icon}
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export const LIST_HIERARCHY_TOGGLE_OPTIONS = HIERARCHY_OPTIONS;
